import { useCallback } from 'react';
import { useApiClient } from '../../../contexts/ApiContext';
import { toApiLoadout, toCreateGameRequest, toJoinGameRequest, toApiAction } from './rushMidAdapters';
import { toDomainSnapshot } from '../../../services/wireCast';
import type {
  Action, FighterInit, Loadout, RoundEvent, Team, RushMidMap,
  RushMidSnapshot, RushMidSnapshotPlaying, RushMidSnapshotOver,
  RushMidSpectatePlaying, RushMidSpectateOver,
  RushMidPublicFighter, RushMidScoreEntry, RushMidBuyRosterEntry, RushMidPayout,
  RushMidRoundAction,
} from '@cs-shot/battle-sim';

export type Side = Team;
export type SidePref = Side | 'any';
export type { RushMidSnapshot };
export type Ally = RushMidBuyRosterEntry;
export type PublicFighter = RushMidPublicFighter;
export type ScoreEntry = RushMidScoreEntry;

export interface RoundUpdate {
  matchId: string;
  map: RushMidMap;
  round: number;
  events: RoundEvent[];
  actions?: Record<string, RushMidRoundAction>;
  fighters: PublicFighter[];
  initFighters?: PublicFighter[];
  inits?: FighterInit[];
  yourFighterId: string | null;
  yourActionSubmitted?: boolean;
  deadline: number | null;
  over: boolean;
  winner?: Team | null;
  payout?: RushMidPayout;
  scoreboard?: ScoreEntry[];
  serverSeed?: string;
  serverSeedHash?: string;
  clientSeed?: string;
}

export const toSnapshot = toDomainSnapshot;

export function snapshotToRoundUpdate(s: RushMidSnapshotPlaying | RushMidSnapshotOver): RoundUpdate {
  const base = {
    matchId: s.matchId,
    map: s.map,
    round: s.round,
    events: s.lastEvents,
    actions: s.lastActions,
    fighters: s.fighters,
    inits: s.inits,
    yourFighterId: s.yourFighterId,
  };
  if (s.status === 'OVER') {
    return {
      ...base, yourActionSubmitted: false, deadline: null, over: true,
      winner: s.winner, payout: s.payout, scoreboard: s.scoreboard,
      serverSeed: s.serverSeed, serverSeedHash: s.serverSeedHash, clientSeed: s.clientSeed,
    };
  }
  return { ...base, yourActionSubmitted: s.yourActionSubmitted, deadline: s.roundDeadline, over: false };
}

export function spectateToRoundUpdate(v: RushMidSpectatePlaying | RushMidSpectateOver): RoundUpdate {
  const base = {
    matchId: v.matchId,
    map: v.map,
    round: v.round,
    events: v.lastEvents,
    actions: v.lastActions,
    fighters: v.fighters,
    inits: v.inits,
    yourFighterId: null,
  };
  if (v.status === 'OVER') {
    return {
      ...base, yourActionSubmitted: false, deadline: null, over: true,
      winner: v.winner, scoreboard: v.scoreboard,
      serverSeed: v.serverSeed, serverSeedHash: v.serverSeedHash, clientSeed: v.clientSeed,
    };
  }
  return { ...base, yourActionSubmitted: false, deadline: v.roundDeadline, over: false };
}

export function snapshotAllies(s: RushMidSnapshot): Ally[] {
  return s.status === 'BUYING' ? s.allies : [];
}

export function useRushMidMatch() {
  const apiClient = useApiClient();

  const createGame = useCallback(
    async (mode: number, ticket: number, side: SidePref, matchMode?: 'ranked' | 'bots') =>
      toSnapshot(await apiClient.api.rushMidControllerCreateOpenGame(toCreateGameRequest(mode, ticket, side, matchMode))),
    [apiClient],
  );
  const joinGame = useCallback(
    async (matchId: string, side: Side) =>
      toSnapshot(await apiClient.api.rushMidControllerJoinOpenGame(matchId, toJoinGameRequest(side))),
    [apiClient],
  );
  const leaveGame = useCallback(
    async () => toSnapshot(await apiClient.api.rushMidControllerLeaveOpenGame()),
    [apiClient],
  );
  const submitLoadout = useCallback(
    async (matchId: string, loadout: Loadout, ready: boolean) =>
      toSnapshot(await apiClient.api.rushMidControllerSubmitLoadout(matchId, { loadout: toApiLoadout(loadout), ready })),
    [apiClient],
  );
  const submitAction = useCallback(
    async (matchId: string, action: Action, round?: number) =>
      toSnapshot(await apiClient.api.rushMidControllerSubmitAction(matchId, toApiAction(action, round))),
    [apiClient],
  );
  const leaveMatch = useCallback(
    async (matchId: string) => toSnapshot(await apiClient.api.rushMidControllerLeaveMatch(matchId)),
    [apiClient],
  );
  const getSnapshot = useCallback(
    async () => toSnapshot(await apiClient.api.rushMidControllerSnapshot()),
    [apiClient],
  );

  return { createGame, joinGame, leaveGame, submitLoadout, submitAction, leaveMatch, getSnapshot };
}
